import React from 'react';
import { Layout, ConfigProvider, theme } from 'antd';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { initQueryPersistence } from './utils/cache/persist';
import { useLocation, useRoutes } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import enUS from 'antd/locale/en_US';
import trTR from 'antd/locale/tr_TR';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import AppHeader from './components/layout/AppHeader';
// Tablo yardımcı sınıfları (zebra/left-align/compact) lazy() ile bölünen sayfalarda da
// deterministik yüklensin diye burada eager import edilir.
import './components/common/table.css';
import AppContent from './components/layout/AppContent';
import AppFooter from './components/layout/AppFooter';
import {getFlattenedRoutes} from './routes/index';
import { AuthProvider, useAuth, REMEMBER_STORAGE_KEY } from './components/common/security/AuthContext';
import GlobalCommandPalette from './components/layout/GlobalCommandPalette';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage } from './i18n/constants';

const { Header, Content, Footer } = Layout;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Initialize opt-in persistence for queries marked with meta.persist === true
initQueryPersistence(queryClient, {
  storageKey: 'rq-cache',
  maxAgeMs: 7 * 24 * 60 * 60 * 1000,
  shouldDehydrateQuery: (q) => q?.meta?.persist === true,
});

// Sadece AuthProvider ALTINDA kullanılacak inaktivite izleyicisi
const InactivityWatcher: React.FC = () => {
  const { logout, token } = useAuth();
  // logout her render'da yeniden oluşuyor; ref ile tutulur ki sayaç gereksiz yere baştan kurulmasın
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    // Oturum yoksa ya da "Remember me" seçildiyse hareketsizlik çıkışı uygulanmaz
    if (!token || localStorage.getItem(REMEMBER_STORAGE_KEY) === '1') {
      return;
    }

    let inactivityTimeout: ReturnType<typeof setTimeout>;

    const handleLogout = () => {
      // Merkezi AuthContext logout'unu kullan
      logoutRef.current();
    };

    const resetInactivityTimer = () => {
      clearTimeout(inactivityTimeout);
      // 60 dakika hareketsizlikten sonra otomatik logout
      inactivityTimeout = setTimeout(handleLogout, 60 * 60 * 1000);
    };

    // Daha geniş aktivite algılama: mouse hareketi, klavye, tıklama, scroll
    window.addEventListener('mousemove', resetInactivityTimer);
    window.addEventListener('keydown', resetInactivityTimer);
    window.addEventListener('click', resetInactivityTimer);
    window.addEventListener('scroll', resetInactivityTimer);

    resetInactivityTimer();

    return () => {
      window.removeEventListener('mousemove', resetInactivityTimer);
      window.removeEventListener('keydown', resetInactivityTimer);
      window.removeEventListener('click', resetInactivityTimer);
      window.removeEventListener('scroll', resetInactivityTimer);
      clearTimeout(inactivityTimeout);
    };
  }, [token]);

  return null;
};

const App: React.FC = () => {
  useRoutes(getFlattenedRoutes());
  const location = useLocation();
  const { i18n } = useTranslation();
  // Kaldırıldı: F5 ile aynı sayfada kalmayı engelliyordu

  const hasHeader = location.pathname !== '/login' && location.pathname !== '/';
  const isLogin = location.pathname === '/login';
  const contentPadding = isLogin ? 0 : 24;
  const path = location.pathname;
  const hideFooter =
    path.endsWith('/forms/create') ||
    path.endsWith('/forms/definitions/create') ||
    path.includes('/forms/definitions/edit/') ||
    path.endsWith('/pdf-to-react');
  const showFooter = location.pathname !== '/login' && !hideFooter;

  const appLang = normalizeLanguage(i18n.resolvedLanguage ?? i18n.language);
  const antdLocale = appLang === 'tr' ? trTR : enUS;

  useEffect(() => {
    dayjs.locale(appLang === 'tr' ? 'tr' : 'en');
  }, [appLang]);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        {/* AuthProvider altında çalışacak global inaktivite izlemesi */}
        <InactivityWatcher />
        <GlobalCommandPalette />
        <ConfigProvider
          locale={antdLocale}
          theme={{
            algorithm: theme.defaultAlgorithm,
            token: {
              colorPrimary: '#1677ff',
              borderRadius: 6,
            },
          }}
        >
          <Layout className={hasHeader ? 'layout' : 'layout layout--no-header'} style={{ minHeight: '100vh' }}>
            {hasHeader && (
              <Header
                style={{
                  padding: 0,
                  height: 64,
                  position: 'fixed',
                  top: 0,
                  width: '100%',
                  zIndex: 1000,
                }}
              >
                <AppHeader />
              </Header>
            )}
            <Content
              style={{
                padding: contentPadding,
                backgroundColor: isLogin ? '#f6f7f8' : '#fff',
                // Header (64px) + olası alt menü (40px) için ekstra boşluk,
                // böylece alt menü çizgisi sayfa başlığına karışmaz.
                marginTop: hasHeader ? 104 : 0,
              }}
            >
              <AppContent />
            </Content>
            {showFooter && (
              <Footer>
                <AppFooter />
              </Footer>
            )}
          </Layout>
        </ConfigProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;